// src/utilities/formRegister.js — SOUND-709
//
// react-hook-form's `register()` returns { name, onChange, onBlur, ref }. Spread
// it onto an input and then declare your own `onChange` AFTER, and React's
// last-prop-wins REPLACES RHF's handler. The field still LOOKS registered — it
// has the name and the ref — so validation that reads the DOM can pass while
// the value never reaches the submitted form data.
//
// That is not hypothetical. It broke self-serve signup in production: the
// business-name input declared `required`, the user typed a name, validation
// passed, and POST /users received `{ email }` with no companyName. The API
// rejected it and rolled back the Firebase user, so every self-serve signup
// dead-ended. Eleven days, and it was invisible because internal admins kept
// creating client companies by hand — the companies table looked healthy while
// the users table had not gained a real self-serve merchant since 2026-08-14.
//
// Chain, never replace.

/**
 * Merge extra handlers onto a react-hook-form field WITHOUT clobbering RHF's.
 *
 * @param {object} field  the object returned by register(name, rules)
 * @param {object} extra  additional handlers, e.g. { onChange, onBlur }
 * @returns {object} props to spread onto the input
 *
 * @example
 *   <input {...withHandlers(register("companyName", { required: "…" }), {
 *     onChange: () => clearErrors("companyName"),
 *   })} />
 */
export function withHandlers(field, extra = {}) {
  if (!field || typeof field !== "object") {
    throw new Error("withHandlers requires the object returned by register()");
  }
  const merged = { ...field };
  for (const [key, fn] of Object.entries(extra)) {
    if (typeof fn !== "function") continue;
    const original = field[key];
    merged[key] =
      typeof original === "function"
        ? (...args) => {
            // RHF FIRST. It records the value; the extra handler is a UI
            // nicety. If the extra one throws, RHF has already recorded.
            const result = original(...args);
            fn(...args);
            return result;
          }
        : fn;
  }
  return merged;
}


/**
 * Assemble the arguments a signup submit handler needs from react-hook-form's
 * data object.
 *
 * EXTRACTED BECAUSE THIS IS WHERE THE PRODUCTION BUG LIVED. AuthForm's onSubmit
 * destructured `{ email, pass }` and built its payload from those two names, so
 * `companyName` — rendered, validated and tracked correctly — was dropped at the
 * narrow point between the form and the handler. POST /users received
 * `{ email }`, the API rejected it, and self-serve signup dead-ended for eleven
 * days.
 *
 * A destructure is a silent allowlist. Anything not named is discarded, and
 * nothing in the type system, the build or the tests notices. Naming the
 * contract here makes it assertable.
 *
 * @param {object} data  the object react-hook-form passes to handleSubmit
 * @returns {{email:string, pass:string, companyName?:string}}
 */
export function buildSignupArgs(data = {}) {
  const { email, pass, companyName } = data;
  return {
    email,
    pass,
    // Kept even when blank so the caller can tell "not collected" from
    // "collected and empty" — an invited signup legitimately has no business
    // name, and the API distinguishes those cases.
    ...(companyName !== undefined && { companyName }),
  };
}
